/**
 * Feedback (temporary, dev-phase): the client's in-app tracker for feature requests, changes, bugs
 * and improvement ideas, replacing e-mail threads while NexSeed is being built. Design and every
 * rule below: docs/designs/feedback-module.md (approved 2026-09-25, eng review 2026-09-26/28).
 *
 * The API enforces these rules and the web uses the same functions to decide which buttons to
 * show, so the two cannot drift apart. Only `feedback.manage` moves an item between statuses:
 *
 *                pick up (actor joins the assigned developers)
 *     PENDING ─────────────────────────▶ IN_PROGRESS ◀──────────┐
 *        │                                │   │    ▲             │
 *        │ reject (text*)         ask     │   │    │ resume      │ reopen (text*)
 *        ▼                        (text*) ▼   │    │             │
 *     REJECTED ◀──── reject (text*) ──────┘   ▼    │             │
 *     (final)                         DISCUSSION_NEEDED          │
 *                                                                 │
 *                          resolve (text optional)                │
 *                IN_PROGRESS ──────────────────────────▶ RESOLVED ┘
 *
 * Removable with the module: nothing outside feedback* files imports this.
 */
import { z } from "zod";

export const FEEDBACK_STATUSES = [
  "pending",
  "in_progress",
  "discussion_needed",
  "resolved",
  "rejected",
] as const;
export type FeedbackStatus = (typeof FEEDBACK_STATUSES)[number];
export const FEEDBACK_STATUS_LABELS: Record<FeedbackStatus, string> = {
  pending: "Pending",
  in_progress: "In Progress",
  discussion_needed: "Discussion Needed",
  resolved: "Resolved",
  rejected: "Rejected",
};
/** The statuses the "Open" list tab groups together. */
export const FEEDBACK_OPEN_STATUSES: readonly FeedbackStatus[] = [
  "pending",
  "in_progress",
  "discussion_needed",
];

export const FEEDBACK_TYPES = [
  "feature",
  "change",
  "bug",
  "improvement",
  "test",
] as const;
export type FeedbackType = (typeof FEEDBACK_TYPES)[number];
export const FEEDBACK_TYPE_LABELS: Record<FeedbackType, string> = {
  feature: "Feature request",
  change: "Change",
  bug: "Bug",
  improvement: "Improvement",
  test: "Test",
};

export const FEEDBACK_PRIORITIES = [
  "low",
  "medium",
  "high",
  "critical",
] as const;
export type FeedbackPriority = (typeof FEEDBACK_PRIORITIES)[number];
export const FEEDBACK_PRIORITY_LABELS: Record<FeedbackPriority, string> = {
  low: "Low",
  medium: "Medium",
  high: "High",
  critical: "Critical",
};

export const FEEDBACK_EVENT_KINDS = [
  "created",
  "edited",
  "status_changed",
  "owner_changed",
  "assignee_added",
  "assignee_removed",
  "priority_changed",
  "member_added",
  "member_removed",
  "comment_added",
  "attachment_added",
  "attachment_removed",
] as const;
export type FeedbackEventKind = (typeof FEEDBACK_EVENT_KINDS)[number];

/** R8 and E6 limits. */
export const FEEDBACK_LIMITS = {
  itemFiles: 5,
  commentFiles: 5,
  draftsPerUser: 5,
  members: 20,
  assignees: 10,
  /** Composer uploads older than this are never shown, linked (409 draft_expired) or kept. */
  draftTtlMs: 24 * 60 * 60 * 1000,
  titleMax: 200,
  descriptionMax: 10_000,
  commentMax: 10_000,
  reasonMax: 2_000,
} as const;

/** Displayed number: FB-12. */
export const feedbackNo = (id: number) => `FB-${id}`;
/** "FB-12", "fb 12" or "12" → 12; anything else → null. */
export const parseFeedbackNo = (q: string): number | null => {
  const m = /^\s*(?:fb[-\s]?)?(\d{1,9})\s*$/i.exec(q);
  return m ? Number(m[1]) : null;
};

// ---- transitions -------------------------------------------------------------------------------

export const FEEDBACK_ACTIONS = [
  "pick_up",
  "reject",
  "ask",
  "resume",
  "resolve",
  "reopen",
] as const;
export type FeedbackAction = (typeof FEEDBACK_ACTIONS)[number];

/** How a transition treats its `text`: where it lands is the API's business (see the design). */
export type TextRule = "required" | "optional" | "none";

interface Transition {
  action: FeedbackAction;
  from: FeedbackStatus;
  to: FeedbackStatus;
  text: TextRule;
}

export const FEEDBACK_TRANSITIONS: readonly Transition[] = [
  { action: "pick_up", from: "pending", to: "in_progress", text: "none" },
  { action: "reject", from: "pending", to: "rejected", text: "required" },
  { action: "reject", from: "in_progress", to: "rejected", text: "required" },
  {
    action: "ask",
    from: "in_progress",
    to: "discussion_needed",
    text: "required",
  },
  {
    action: "resume",
    from: "discussion_needed",
    to: "in_progress",
    text: "optional",
  },
  { action: "resolve", from: "in_progress", to: "resolved", text: "optional" },
  { action: "reopen", from: "resolved", to: "in_progress", text: "required" },
];

export const FEEDBACK_ACTION_LABELS: Record<FeedbackAction, string> = {
  pick_up: "Pick up",
  reject: "Reject",
  ask: "Ask the client",
  resume: "Resume",
  resolve: "Resolve",
  reopen: "Reopen",
};

/** The transition from → to, or null when it is not allowed (409 invalid_transition). */
export const findFeedbackTransition = (
  from: FeedbackStatus,
  to: FeedbackStatus
): Transition | null =>
  FEEDBACK_TRANSITIONS.find((t) => t.from === from && t.to === to) ?? null;

export const canTransition = (from: FeedbackStatus, to: FeedbackStatus) =>
  findFeedbackTransition(from, to) !== null;

/** Transitions that post their text as a comment (E10: each such comment writes comment_added). */
export const transitionPostsComment = (t: Transition) =>
  t.action === "ask" ||
  t.action === "resume" ||
  t.action === "resolve" ||
  t.action === "reopen";

/** E4: reject and reopen keep their reason on the item; every other transition clears it. */
export const transitionKeepsReason = (t: Transition) =>
  t.action === "reject" || t.action === "reopen";

// ---- who may do what --------------------------------------------------------------------------

/** The viewer's relation to one item. `canManage` = holds feedback.manage. */
export interface FeedbackViewer {
  userId: number;
  canManage: boolean;
}
export interface FeedbackItemRef {
  status: FeedbackStatus;
  createdBy: number;
  /** The developers the item is assigned to (the "To" line). */
  assigneeIds: readonly number[];
  /** CC colleagues. */
  memberIds: readonly number[];
}

/** R1: creator, assigned developers and CC colleagues see an item; feedback.manage sees all. */
export const canSeeFeedback = (v: FeedbackViewer, item: FeedbackItemRef) =>
  v.canManage ||
  item.createdBy === v.userId ||
  item.assigneeIds.includes(v.userId) ||
  item.memberIds.includes(v.userId);

const isCreator = (v: FeedbackViewer, item: FeedbackItemRef) =>
  item.createdBy === v.userId;

/** R5 + matrix: title, description, type. */
export const canEditDetails = (v: FeedbackViewer, item: FeedbackItemRef) => {
  if (item.status === "pending") return v.canManage || isCreator(v, item);
  if (item.status === "in_progress" || item.status === "discussion_needed")
    return v.canManage;
  return false;
};

/** R6 + matrix: priority after creation is the tech team's. */
export const canChangePriority = (v: FeedbackViewer, item: FeedbackItemRef) =>
  v.canManage &&
  (item.status === "pending" ||
    item.status === "in_progress" ||
    item.status === "discussion_needed");

/** R2 + matrix: assigned developers change on everything but Rejected; none only while Pending. */
export const canChangeAssignees = (v: FeedbackViewer, item: FeedbackItemRef) =>
  v.canManage && item.status !== "rejected";
export const assigneesMayBeEmpty = (status: FeedbackStatus) =>
  status === "pending";

/** R7 + matrix: creator and manage add/remove members; anyone may leave; never on Rejected. */
export const canManageMembers = (v: FeedbackViewer, item: FeedbackItemRef) =>
  item.status !== "rejected" && (v.canManage || isCreator(v, item));
export const canLeave = (v: FeedbackViewer, item: FeedbackItemRef) =>
  item.status !== "rejected" && item.memberIds.includes(v.userId);

/** Matrix: comments and attachments from anyone who can see the item, except on Rejected. */
export const canComment = (v: FeedbackViewer, item: FeedbackItemRef) =>
  item.status !== "rejected" && canSeeFeedback(v, item);
export const canAttach = canComment;

/** E8: an item-level file may be removed by its uploader or manage while the item is open. */
export const canRemoveItemFile = (
  v: FeedbackViewer,
  item: FeedbackItemRef,
  uploadedBy: number
) =>
  (FEEDBACK_OPEN_STATUSES as readonly string[]).includes(item.status) &&
  (v.canManage || uploadedBy === v.userId) &&
  canSeeFeedback(v, item);

export interface FeedbackAllowed {
  transitions: FeedbackAction[];
  edit: boolean;
  priority: boolean;
  assignees: boolean;
  members: boolean;
  leave: boolean;
  comment: boolean;
  attach: boolean;
}

/** Everything this viewer may do to this item right now — the web's buttons, the API's gate. */
export const feedbackAllowedActions = (
  v: FeedbackViewer,
  item: FeedbackItemRef
): FeedbackAllowed => {
  const sees = canSeeFeedback(v, item);
  return {
    transitions:
      sees && v.canManage
        ? FEEDBACK_TRANSITIONS.filter((t) => t.from === item.status).map(
            (t) => t.action
          )
        : [],
    edit: sees && canEditDetails(v, item),
    priority: sees && canChangePriority(v, item),
    assignees: sees && canChangeAssignees(v, item),
    members: sees && canManageMembers(v, item),
    leave: sees && canLeave(v, item),
    comment: canComment(v, item),
    attach: canAttach(v, item),
  };
};

// ---- request bodies (API validates, web forms reuse) ---------------------------------------------

const title = z
  .string()
  .trim()
  .min(3, "Title needs at least 3 characters")
  .max(FEEDBACK_LIMITS.titleMax);
const description = z
  .string()
  .trim()
  .min(1, "Describe the request")
  .max(FEEDBACK_LIMITS.descriptionMax);

export const createFeedbackBody = z.object({
  title,
  description,
  type: z.enum(FEEDBACK_TYPES),
  priority: z.enum(FEEDBACK_PRIORITIES),
  /** The developers it is assigned to: the form ticks every developer by default. */
  assigneeIds: z
    .array(z.number().int().positive())
    .min(1, "Pick at least one developer")
    .max(FEEDBACK_LIMITS.assignees),
  /** CC colleagues. */
  memberIds: z
    .array(z.number().int().positive())
    .max(FEEDBACK_LIMITS.members)
    .default([]),
});
export type CreateFeedbackBody = z.infer<typeof createFeedbackBody>;

export const updateFeedbackBody = z
  .object({
    title: title.optional(),
    description: description.optional(),
    type: z.enum(FEEDBACK_TYPES).optional(),
    priority: z.enum(FEEDBACK_PRIORITIES).optional(),
  })
  .refine((b) => Object.values(b).some((x) => x !== undefined), {
    message: "Nothing to change",
  });
export type UpdateFeedbackBody = z.infer<typeof updateFeedbackBody>;

export const feedbackStatusBody = z.object({
  to: z.enum(FEEDBACK_STATUSES),
  text: z.string().trim().max(FEEDBACK_LIMITS.reasonMax).optional(),
});
export type FeedbackStatusBody = z.infer<typeof feedbackStatusBody>;

export const feedbackCommentBody = z.object({
  body: z.string().trim().min(1).max(FEEDBACK_LIMITS.commentMax),
  attachmentIds: z
    .array(z.number().int().positive())
    .max(FEEDBACK_LIMITS.commentFiles)
    .default([]),
});
export type FeedbackCommentBody = z.infer<typeof feedbackCommentBody>;

export const feedbackUploadBody = z.object({
  target: z.enum(["item", "draft"]),
  fileName: z.string().trim().min(1).max(255),
  mimeType: z.string().trim().max(255).default("application/octet-stream"),
  dataBase64: z.string().min(1),
});
export type FeedbackUploadBody = z.infer<typeof feedbackUploadBody>;

// ---- list -------------------------------------------------------------------------------------

export const FEEDBACK_TABS = [
  "waiting",
  "updated",
  "assigned",
  "open",
  "pending",
  "in_progress",
  "discussion_needed",
  "resolved",
  "rejected",
  "all",
] as const;
export type FeedbackTab = (typeof FEEDBACK_TABS)[number];
export const FEEDBACK_TAB_LABELS: Record<FeedbackTab, string> = {
  waiting: "Waiting on me",
  updated: "Updated for me",
  assigned: "Assigned to me",
  open: "Open",
  pending: "Pending",
  in_progress: "In Progress",
  discussion_needed: "Discussion Needed",
  resolved: "Resolved",
  rejected: "Rejected",
  all: "All",
};
